/**
 * FmBarChart — the bar engine: single, grouped and stacked bars, including
 * twin assortment-by-species stacks (distinct `stackKey`s per category).
 *
 * Colour comes from each series' descriptor (species by code, assortment,
 * economics, tone). `activeSeriesKey` dims the other series; `activeCategory`
 * dims only the cells outside that category. Category picking works by click
 * and by keyboard through the Recharts accessibility layer.
 *
 * With a reference-role series the opacity step is what tells result from
 * reference, so dimming would fold both bars of an inactive category into one
 * shade and contradict the legend. Such a chart marks its active category with
 * a background band and leaves every bar at its role's opacity.
 */
import React from 'react';
import { Bar, BarChart, CartesianGrid, ReferenceArea, Tooltip, XAxis, YAxis } from 'recharts';
import type { FmBarChartSpec, FmChartSize } from './chartTypes';
import {
  CHART_DIMMED_OPACITY,
  CHART_GRID_PROPS,
  chartMargin,
  CHART_XAXIS_PROPS,
  CHART_YAXIS_PROPS,
  xAxisTitle,
  yAxisTitle,
} from './FmChartChrome.constants';
import { FmChartTooltip } from './FmChartChrome';
import { barCellShape } from './barCellShape';
import { FmChartFrame } from './FmChartFrame';
import { categoryAxisLayout, categoryAxisProps } from './categoryAxis';
import { useChartCategorySelection } from './useChartCategorySelection';
import { paletteEvidenceRoleOpacity } from './palette';
import { resolveSeriesColor } from './seriesColor';

export type FmBarChartProps = Omit<FmBarChartSpec, 'kind'> & {
  size?: FmChartSize;
  'data-testid'?: string;
};

const MAX_BAR_SIZE = 72;

function cellOpacity(
  seriesOpacity: number,
  category: string,
  activeCategory: string | undefined,
): number {
  if (activeCategory === undefined || category === activeCategory) return seriesOpacity;
  // Dimming replaces the reference-role step instead of compounding it: 0.58 ×
  // 0.35 left reference stacks of inactive periods unreadable on dark bands.
  return Math.min(seriesOpacity, CHART_DIMMED_OPACITY);
}

export const FmBarChart: React.FC<FmBarChartProps> = ({
  data,
  xKey,
  xLabel,
  yLabel,
  series,
  activeSeriesKey,
  activeCategory,
  onSelectCategory,
  formatValue,
  size = 'md',
  'data-testid': testId,
}) => {
  const selection = useChartCategorySelection(onSelectCategory, xKey);
  const rows = data.map((row) => ({ ...row }));
  const stacked = series.some((entry) => entry.stackKey !== undefined);
  const [frameWidth, setFrameWidth] = React.useState<number | null>(null);
  const categories = rows.map((row) => String(row[xKey] ?? ''));
  const axis = categoryAxisLayout(categories, frameWidth);
  const hasReference = series.some((entry) => entry.role === 'reference');
  const bandCategory =
    hasReference && activeCategory !== undefined && categories.includes(activeCategory)
      ? activeCategory
      : undefined;
  const dimCategory = hasReference ? undefined : activeCategory;
  const roleByKey = Object.fromEntries(series.map((entry) => [entry.dataKey, entry.role]));
  return (
    <FmChartFrame
      size={size}
      onKeyDown={selection.onKeyDown}
      onWidth={setFrameWidth}
      data-testid={testId}
      data-kind="bar"
    >
      <BarChart
        data={rows}
        margin={chartMargin(yLabel)}
        maxBarSize={MAX_BAR_SIZE}
        barCategoryGap={stacked ? '18%' : '12%'}
        barGap={2}
        accessibilityLayer
        onClick={selection.onClick}
      >
        <CartesianGrid {...CHART_GRID_PROPS} />
        <XAxis
          dataKey={xKey}
          {...CHART_XAXIS_PROPS}
          {...categoryAxisProps(axis)}
          // One category names itself in its tick; an axis title over a
          // single bar would only repeat it.
          label={categories.length > 1 ? xAxisTitle(xLabel) : undefined}
        />
        <YAxis {...CHART_YAXIS_PROPS} label={yAxisTitle(yLabel)} />
        {bandCategory !== undefined ? (
          <ReferenceArea
            x1={bandCategory}
            x2={bandCategory}
            fill="var(--fm-d-surface-3)"
            fillOpacity={1}
            stroke="none"
            ifOverflow="hidden"
          />
        ) : null}
        <Tooltip
          content={<FmChartTooltip formatValue={formatValue} roleByKey={roleByKey} />}
          cursor={{ fill: 'var(--fm-d-surface-3)' }}
        />
        {series.map((entry) => {
          const fill = resolveSeriesColor(entry.color);
          const roleOpacity = paletteEvidenceRoleOpacity(entry.role);
          const seriesOpacity =
            activeSeriesKey !== undefined && activeSeriesKey !== entry.dataKey
              ? Math.min(roleOpacity, CHART_DIMMED_OPACITY)
              : roleOpacity;
          return (
            <Bar
              key={entry.dataKey}
              dataKey={entry.dataKey}
              name={entry.label}
              fill={fill}
              stackId={entry.stackKey}
              isAnimationActive={false}
              onClick={selection.onBarClick}
              shape={barCellShape<Record<string, unknown>>((datum) => ({
                fill,
                fillOpacity: cellOpacity(seriesOpacity, String(datum[xKey] ?? ''), dimCategory),
              }))}
            />
          );
        })}
        {selection.reporter}
      </BarChart>
    </FmChartFrame>
  );
};
